import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import CreateUserModal from '../components/CreateUserModal';
import UserTestPermissionPanel from '../components/UserTestPermissionPanel';

const UserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showPermissionPanel, setShowPermissionPanel] = useState(false);
    const { encryptedApiCall, API_BASE_URL, logout, isAdmin } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAdmin) {
            navigate('/categories');
            return;
        }
        fetchUsers();
    }, [isAdmin]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const data = await encryptedApiCall(`${API_BASE_URL}/admin/users`, {
                method: 'GET'
            });
            setUsers(data);
        } catch (err) {
            if (err.message && err.message.includes('401')) {
                logout();
                navigate('/login');
                return;
            }
            setError(err.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = () => {
        setShowCreateModal(true);
    };

    const handleUserCreated = () => {
        setShowCreateModal(false);
        fetchUsers(); // Refresh list
    };

    const handleManagePermissions = (user) => {
        setSelectedUser(user);
        setShowPermissionPanel(true);
    };

    const handlePermissionsUpdated = () => {
        setShowPermissionPanel(false);
        setSelectedUser(null);
        fetchUsers(); // Refresh to update test counts
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" />
            </Container>
        );
    }

    return (
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => navigate('/categories')} 
                        className="me-2"
                    >
                        ← Back to Categories
                    </Button>
                    <h2 className="d-inline ms-3">User Management</h2>
                </div>
                <Button variant="primary" onClick={handleCreateUser}>
                    + Create User
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {users.length === 0 ? (
                <Alert variant="info">No users found.</Alert>
            ) : (
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Username</th>
                            <th>Role</th>
                            <th>Assigned Tests</th>
                            <th>Created At</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                                <td>{user.id}</td>
                                <td>{user.username}</td>
                                <td>
                                    <Badge bg={user.role === 'ADMIN' ? 'danger' : 'primary'}>
                                        {user.role}
                                    </Badge>
                                </td>
                                <td>{user.test_count || 0}</td>
                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                <td>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleManagePermissions(user)}
                                    >
                                        Manage Tests
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <CreateUserModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                onUserCreated={handleUserCreated}
            />

            {selectedUser && (
                <UserTestPermissionPanel
                    show={showPermissionPanel}
                    onHide={() => {
                        setShowPermissionPanel(false);
                        setSelectedUser(null);
                    }}
                    user={selectedUser}
                    onPermissionsUpdated={handlePermissionsUpdated}
                />
            )}
        </Container>
    );
};

export default UserManagementPage;

